import React, { memo, useMemo } from 'react'
import moment from 'moment'
import { View, Linking, TextStyle } from 'react-native'
import Communications from 'react-native-communications'
import Animated, { FadeIn, Layout } from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useActionSheet } from '@expo/react-native-action-sheet'
import Clipboard from '@react-native-clipboard/clipboard'
import ParsedText from 'react-native-parsed-text'
import { useTheme } from 'styled-components/native'
import styled from 'styled-components/native'

import { MessageContainer } from 'APP/Converse/Chat/MessageContainer'
import Typography from 'APP/Converse/Typography'
import { useDispatch } from 'APP/Hooks/redux'
import { useChatContext, usePostContext } from 'APP/Lib/Context'
import PatientHistoryActions from 'APP/Redux/PatientHistoryRedux'
import Analytics from 'APP/Services/Analytics'
import I18n from 'APP/I18n'
import { ApplicationStyles } from 'APP/Themes'

const WWW_URL_PATTERN = /^www\./i

const DateContainer = styled(Animated.View)`
  height: 12px;
  margin-top: ${({ theme }) => theme.metrics.baseMargin / 8}px;
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin / 4}px;
`

const TextMessage: React.FC = () => {
  const theme = useTheme()
  const {
    post,
    isMember,
    previousPostIsSameUser,
    nextPostIsSameUser,
    previousPostIsSameDay,
    nextPostIsSameDay,
    nextPostSameTimestamp,
  } = usePostContext()

  const { channelId } = useChatContext()
  const insets = useSafeAreaInsets()
  const { showActionSheetWithOptions } = useActionSheet()
  const dispatch = useDispatch()

  const actionSheetContainerStyle = useMemo(
    () => ({
      ...ApplicationStyles.actionSheet.containerStyle,
      paddingBottom: insets.bottom,
    }),
    [insets.bottom]
  )

  if (!post) return null

  const error = post.props?.failed
  const date = moment(post.create_at).format('LT')
  const showTimeStamp = !nextPostIsSameUser || !nextPostSameTimestamp

  const handleURL = (url: string) => {
    if (WWW_URL_PATTERN.test(url)) {
      handleURL(`https://${url}`)
    } else {
      Linking.canOpenURL(url).then((supported) => {
        if (supported) {
          Linking.openURL(url)
        }
      })
    }
  }

  const handlePhone = (phone: string) => {
    const options = [
      I18n.t('ConversationScreen.actionSheets.call'),
      I18n.t('ConversationScreen.actionSheets.text'),
      I18n.t('ConversationScreen.actionSheets.cancel'),
    ]
    const cancelButtonIndex = options.length - 1
    showActionSheetWithOptions(
      {
        ...ApplicationStyles.actionSheet,
        options,
        cancelButtonIndex,
        containerStyle: actionSheetContainerStyle,
      },
      (buttonIndex?: number) => {
        switch (buttonIndex) {
          case 0:
            Communications.phonecall(phone, true)
            break
          case 1:
            Communications.text(phone)
            break
          default:
            break
        }
      }
    )
  }

  const handleEmail = (email: string) => {
    Communications.email([email], null, null, null, null)
  }

  const handleLongPressError = () => {
    const options = [
      I18n.t('ConversationScreen.actionSheets.retry'),
      I18n.t('ConversationScreen.actionSheets.cancel'),
    ]
    const cancelButtonIndex = options.length - 1

    showActionSheetWithOptions(
      {
        ...ApplicationStyles.actionSheet,
        options,
        cancelButtonIndex,
        containerStyle: actionSheetContainerStyle,
      },
      (buttonIndex?: number) => {
        if (!post.id || !channelId) return
        if (buttonIndex === 0) {
          Analytics.trackEvent('button_click', { button_value: 'Retry send message' })
          // @ts-expect-error FIXME: retryPostRequest is only valid for pending posts, but we have not narrowed the type here
          dispatch(PatientHistoryActions.retryPostRequest(channelId, post.id))
        } else {
          Analytics.trackEvent('button_click', { button_value: 'Cancel send message' })
          // @ts-expect-error FIXME: removePendingPost is only valid for pending posts, but we have not narrowed the type here
          dispatch(PatientHistoryActions.removePendingPost(channelId, post.id))
        }
      }
    )
  }

  const handleLongPress = () => {
    const options = [
      I18n.t('ConversationScreen.actionSheets.copy'),
      I18n.t('ConversationScreen.actionSheets.cancel'),
    ]

    const cancelButtonIndex = options.length - 1
    showActionSheetWithOptions(
      {
        ...ApplicationStyles.actionSheet,
        options,
        cancelButtonIndex,
        containerStyle: actionSheetContainerStyle,
      },
      (buttonIndex?: number) => {
        if (buttonIndex === 0 && post.message) {
          Clipboard.setString(post.message)
        }
      }
    )
  }

  const leftTextStyle: TextStyle = {
    fontFamily: theme.fonts.fontTypes.light,
    fontSize: 16,
    lineHeight: 20,
    color: theme.colors.text,
  }

  const rightTextStyle: TextStyle = {
    fontFamily: theme.fonts.fontTypes.light,
    fontSize: 16,
    lineHeight: 20,
    color: theme.colors.darkText,
  }

  const linkStyle: TextStyle = {
    fontFamily: theme.fonts.fontTypes.base,
    fontWeight: 'bold',
    fontSize: 16,
    lineHeight: 20,
    textDecorationLine: 'underline',
  }

  return (
    <View style={{ alignItems: isMember ? 'flex-end' : 'flex-start' }}>
      <View>
        <MessageContainer
          rtl={isMember}
          isError={Boolean(error)}
          previousPostIsSameUser={previousPostIsSameUser}
          nextPostIsSameUser={nextPostIsSameUser}
          previousPostIsSameDay={previousPostIsSameDay}
          nextPostIsSameDay={nextPostIsSameDay}
        >
          <ParsedText
            style={isMember ? rightTextStyle : leftTextStyle}
            onLongPress={error ? handleLongPressError : handleLongPress}
            parse={[
              { type: 'url', style: linkStyle, onPress: handleURL },
              { type: 'phone', style: linkStyle, onPress: handlePhone },
              { type: 'email', style: linkStyle, onPress: handleEmail },
            ]}
          >
            {post.message}
          </ParsedText>
        </MessageContainer>
      </View>
      {showTimeStamp && (
        <DateContainer entering={FadeIn} layout={Layout}>
          <Typography variant="caption" color="textSecondary">
            {date}
          </Typography>
        </DateContainer>
      )}
    </View>
  )
}

export default memo(TextMessage)
